import type {
  AxisId,
  DatapointMetadata,
  SeriesId,
  XYChartAxisInternal,
} from '../../types/xy-chart-internals.js';
import type { BarDatapoint } from '../../types/xy-chart.js';
import {
  getCombinedAxesOrientationKey,
  getStackingData,
} from '../utils/stacking/stack-datapoints.js';

export interface GroupingResult {
  datapointMetadata: DatapointMetadata;
}

interface GroupData {
  totalPositive: number;
  totalNegative: number;
  axisKey: string;
}

/** Per-axis state tracked during preprocessing.
 * `seriesIds.size === 1` ⇒ single-series axis: `getGroupBinDomain` falls back to
 * `maxBinAbsValue` as a cross-bin reference so the tallest bar represents 100%. */
interface AxisStats {
  seriesIds: Set<SeriesId>;
  maxBinAbsValue: number;
}

/**
 * A class that encapsulates grouping operations for series variants.
 * This class can be instantiated by any series strategy that needs grouping functionality.
 */
export class GroupableSeriesHelper {
  private static readonly STARTING_POINT = 0;
  private readonly groupData: Map<string, GroupData>;
  private readonly axisStats: Map<string, AxisStats>;
  public readonly axisIdToDimension: Map<AxisId, Set<SeriesId>>;

  constructor() {
    this.groupData = new Map();
    this.axisStats = new Map();
    this.axisIdToDimension = new Map();
  }

  /**
   * Calculate total height of group bin.
   * Should be called at the beginning of a new grouping calculation cycle (preprocess phase).
   *
   * Also records which series contributed to each axisKey so that `getGroupBinDomain` can
   * fall back to a cross-bin reference for single-series axes. Single-series groups would
   * otherwise collapse to 100% in every bin.
   */
  processGroupDomain(
    datapoint: BarDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    seriesId: SeriesId,
  ): void {
    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
    );

    const axisKey = getCombinedAxesOrientationKey(
      xAxis.id,
      yAxis.id,
      chartOrientation,
    );
    const stats = this.getOrInitAxisStats(axisKey);
    stats.seriesIds.add(seriesId);

    if (typeof value === 'number') {
      const acc = this.groupData.get(stackKey) ?? {
        totalPositive: GroupableSeriesHelper.STARTING_POINT,
        totalNegative: GroupableSeriesHelper.STARTING_POINT,
        axisKey,
      };
      if (value >= 0) {
        acc.totalPositive += value;
      } else {
        acc.totalNegative += Math.abs(value);
      }
      this.groupData.set(stackKey, acc);

      const binAbsValue = Math.max(acc.totalPositive, acc.totalNegative);
      if (binAbsValue > stats.maxBinAbsValue) {
        stats.maxBinAbsValue = binAbsValue;
      }
    }
  }

  /**
   * Returns the total bin domain for a given group key.
   *
   * For axes with more than one contributing series the result is the per-bin total height
   * of the group (|positive| + |negative|). For axes with only one contributing series the
   * result collapses to a cross-bin reference: the largest per-bin absolute total across all
   * bins of that axis. This keeps single-series groups consistent with the regular relative
   * behavior, where the tallest bar represents 100% and the rest scale proportionally.
   */
  getGroupBinDomain(groupKey: string): number | undefined {
    const groupDomainObj = this.groupData.get(groupKey);
    if (!groupDomainObj) {
      return undefined;
    }
    const stats = this.axisStats.get(groupDomainObj.axisKey);
    if (stats && stats.seriesIds.size === 1) {
      return stats.maxBinAbsValue;
    }
    return groupDomainObj.totalPositive + groupDomainObj.totalNegative;
  }

  private getOrInitAxisStats(axisKey: string): AxisStats {
    let stats = this.axisStats.get(axisKey);
    if (!stats) {
      stats = { seriesIds: new Set(), maxBinAbsValue: 0 };
      this.axisStats.set(axisKey, stats);
    }
    return stats;
  }

  /**
   * Processes a datapoint for grouping, calculating the bin domain metadata.
   * Also registers the series in the dimension map for the primary axis.
   * Returns metadata with binDomain set but no accumulator (bars remain side-by-side).
   */
  processGroupDatapoint(
    datapoint: BarDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    seriesId: string,
    datapointMetadata: DatapointMetadata,
  ): GroupingResult {
    const primaryAxis = chartOrientation === 'vertical' ? xAxis : yAxis;
    const dimension = this.axisIdToDimension.get(primaryAxis.id);

    if (dimension) {
      dimension.add(seriesId);
      this.axisIdToDimension.set(primaryAxis.id, dimension);
    } else {
      this.axisIdToDimension.set(
        primaryAxis.id,
        new Set<string>().add(seriesId),
      );
    }

    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
    );

    if (typeof value !== 'number') {
      return { datapointMetadata };
    }

    const binDomain = this.getGroupBinDomain(stackKey);

    return {
      datapointMetadata: {
        ...datapointMetadata,
        binDomain,
      },
    };
  }
}
